/**
 * Team Redux Reducer
 * Manages team members state
 */

import {
    TEAM_MEMBERS_REQUEST,
    TEAM_MEMBERS_SUCCESS,
    TEAM_MEMBERS_ERROR,
    TEAM_MEMBERS_RESET,
    TEAM_MEMBERS_SET_PARAMS,
    TEAM_REQUESTS_REQUEST,
    TEAM_REQUESTS_SUCCESS,
    TEAM_REQUESTS_ERROR,
    TEAM_REQUESTS_RESET,
    TEAM_REQUESTS_SET_PARAMS,
} from './team.actions';

export const TEAM_REQUESTS_DEFAULT_SORT_BY = 'requested_date';
export const TEAM_REQUESTS_DEFAULT_SORT_ORDER = 'desc';

const initialMembersParams = {
    searchTerm: '',
    page: 1,
    limit: 10,
    sortBy: null,
    sortOrder: null,
    roleFilter: null,
};

const initialRequestsParams = {
    searchTerm: '',
    page: 1,
    limit: 10,
    sortBy: TEAM_REQUESTS_DEFAULT_SORT_BY,
    sortOrder: TEAM_REQUESTS_DEFAULT_SORT_ORDER,
    roleFilter: null,
};

const initialState = {
    members: {
        data: [],
        loading: false,
        error: null,
        pagination: {
            offset: 0,
            limit: 10,
            count: 0,
        },
        params: { ...initialMembersParams },
    },
    requests: {
        data: [],
        loading: false,
        error: null,
        pagination: {
            offset: 0,
            limit: 10,
            count: 0,
        },
        params: { ...initialRequestsParams },
    },
};

/**
 * Team reducer
 * @param {Object} state - Current state
 * @param {Object} action - Action object
 * @returns {Object} New state
 */
const teamReducer = (state = initialState, action) => {
    switch (action.type) {
        case TEAM_MEMBERS_SET_PARAMS:
            return {
                ...state,
                members: {
                    ...state.members,
                    params: {
                        ...state.members.params,
                        ...action.payload,
                    },
                },
            };

        case TEAM_MEMBERS_REQUEST:
            return {
                ...state,
                members: {
                    ...state.members,
                    loading: true,
                    error: null,
                },
            };

        case TEAM_MEMBERS_SUCCESS: {
            const { data, pagination } = action.payload;
            return {
                ...state,
                members: {
                    ...state.members,
                    data,
                    loading: false,
                    error: null,
                    pagination: {
                        offset: pagination?.offset ?? 0,
                        limit: pagination?.limit ?? state.members.pagination.limit,
                        count: pagination?.count ?? 0,
                    },
                },
            };
        }

        case TEAM_MEMBERS_ERROR:
            return {
                ...state,
                members: {
                    ...state.members,
                    loading: false,
                    error: action.payload,
                },
            };

        case TEAM_MEMBERS_RESET:
            return {
                ...state,
                members: {
                    ...initialState.members,
                    params: { ...initialMembersParams },
                },
            };

        // ── Requests (Invitations) ──

        case TEAM_REQUESTS_SET_PARAMS:
            return {
                ...state,
                requests: {
                    ...state.requests,
                    params: {
                        ...state.requests.params,
                        ...action.payload,
                    },
                },
            };

        case TEAM_REQUESTS_REQUEST:
            return {
                ...state,
                requests: {
                    ...state.requests,
                    loading: true,
                    error: null,
                },
            };

        case TEAM_REQUESTS_SUCCESS: {
            const { data: requestsData, pagination: requestsPagination } = action.payload;
            return {
                ...state,
                requests: {
                    ...state.requests,
                    data: requestsData,
                    loading: false,
                    error: null,
                    pagination: {
                        offset: requestsPagination?.offset ?? 0,
                        limit: requestsPagination?.limit ?? state.requests.pagination.limit,
                        count: requestsPagination?.count ?? 0,
                    },
                },
            };
        }

        case TEAM_REQUESTS_ERROR:
            return {
                ...state,
                requests: {
                    ...state.requests,
                    loading: false,
                    error: action.payload,
                },
            };

        case TEAM_REQUESTS_RESET:
            return {
                ...state,
                requests: {
                    ...initialState.requests,
                    params: { ...initialRequestsParams },
                },
            };

        default:
            return state;
    }
};

export default teamReducer;
